/**
 * Two Towns — Free shipping progress bar
 * ---------------------------------------------------------------------------
 * One global controller + a <free-shipping-bar> custom element.
 *
 * Why a custom element: themes replace cart drawer HTML wholesale through the
 * Section Rendering API. Every time a fresh <free-shipping-bar> lands in the
 * DOM, connectedCallback runs automatically — no DOMContentLoaded, no
 * re-binding, no duplicate listeners.
 *
 * How it learns the cart changed (all funnel into one debounced /cart.js read):
 *   1. Watches fetch() and XMLHttpRequest for /cart/add|change|update|clear
 *      — catches every theme and most apps, whatever events they do or don't fire.
 *   2. Listens for common theme cart events (Dawn family, Horizon, etc.).
 *   3. Re-checks when a bar scrolls/opens into view (cart drawer opening),
 *      when the tab becomes visible again, and on back/forward cache restores.
 *
 * Public API (optional, for theme/app code):
 *   window.TwoTownsFreeShipping.refresh()       // re-read /cart.js
 *   window.TwoTownsFreeShipping.update(cartJson) // push a cart object you already have
 */
(function () {
  'use strict';

  // The snippet may be rendered twice (drawer + page), so this file can execute
  // twice. Only the first run installs anything.
  if (window.TwoTownsFreeShipping) return;

  var REFRESH_DEBOUNCE_MS = 100;
  var CART_MUTATION_PATH = /\/cart\/(add|change|update|clear)(\.js|\.json)?\/?$/;
  var THEME_CART_EVENTS = [
    'cart:update', 'cart:updated', 'cart:change', 'cart:changed', 'cart:refresh',
    'cart:build', 'theme:cart:change', 'ajaxProduct:added', 'product:added',
    'cart-drawer:open', 'cart:open', 'drawer:open'
  ];

  var nativeFetch = window.fetch ? window.fetch.bind(window) : null;
  var instances = new Set();
  var state = {
    cart: null,          // { total_price, item_count, currency }
    lastProgress: null,  // 0–1, last value drawn, used to animate across re-renders
    requestSeq: 0,
    appliedSeq: 0,
    timer: null
  };

  /* ---------------------------------------------------------------- utils */

  function rootUrl() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.charAt(root.length - 1) === '/' ? root : root + '/';
  }

  function isCartMutation(url) {
    if (!url) return false;
    try {
      var u = new URL(String(url), window.location.href);
      return u.origin === window.location.origin && CART_MUTATION_PATH.test(u.pathname);
    } catch (e) {
      return false;
    }
  }

  function formatMoney(cents, format) {
    var fmt = String(format || '${{amount}}').replace(/<[^>]*>/g, '');
    var match = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    var placeholder = match ? match[1] : 'amount';

    function withDelimiters(precision, thousands, decimal) {
      thousands = thousands === undefined ? ',' : thousands;
      decimal = decimal === undefined ? '.' : decimal;
      var parts = (Math.abs(cents) / 100).toFixed(precision).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts[1] ? parts[0] + decimal + parts[1] : parts[0];
    }

    var value;
    switch (placeholder) {
      case 'amount_no_decimals': value = withDelimiters(0); break;
      case 'amount_with_comma_separator': value = withDelimiters(2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = withDelimiters(2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator': value = withDelimiters(0, ' '); break;
      case 'amount_with_space_separator': value = withDelimiters(2, ' ', ','); break;
      case 'amount_with_period_and_space_separator': value = withDelimiters(2, ' ', '.'); break;
      default: value = withDelimiters(2);
    }
    return fmt.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  /* ------------------------------------------------------- cart plumbing */

  function applyCart(cart) {
    if (!cart || typeof cart.total_price !== 'number') return;
    state.cart = {
      total_price: cart.total_price,
      item_count: typeof cart.item_count === 'number' ? cart.item_count : 0,
      currency: cart.currency || null
    };
    instances.forEach(function (el) { el.update(state.cart); });
  }

  function fetchCart() {
    if (!nativeFetch) return;
    var seq = ++state.requestSeq;
    nativeFetch(rootUrl() + 'cart.js', {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (cart) {
        // Ignore responses that arrive after a newer request already landed.
        if (!cart || seq < state.appliedSeq) return;
        state.appliedSeq = seq;
        applyCart(cart);
      })
      .catch(function () { /* network hiccup: keep the last good state */ });
  }

  function scheduleRefresh() {
    clearTimeout(state.timer);
    state.timer = setTimeout(fetchCart, REFRESH_DEBOUNCE_MS);
  }

  /* ------------------------------------------- network + event listeners */

  if (nativeFetch) {
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || input;
      var promise = nativeFetch(input, init);
      if (isCartMutation(url)) {
        // Observe completion only — the theme still gets the untouched response.
        promise.then(scheduleRefresh, scheduleRefresh);
      }
      return promise;
    };
  }

  if (window.XMLHttpRequest) {
    var xhrOpen = XMLHttpRequest.prototype.open;
    var xhrSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__fsbCartMutation = isCartMutation(url);
      return xhrOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      if (this.__fsbCartMutation) {
        this.addEventListener('loadend', scheduleRefresh, { once: true });
      }
      return xhrSend.apply(this, arguments);
    };
  }

  THEME_CART_EVENTS.forEach(function (name) {
    document.addEventListener(name, scheduleRefresh);
  });

  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible' && instances.size) scheduleRefresh();
  });

  window.addEventListener('pageshow', function (event) {
    if (event.persisted && instances.size) scheduleRefresh();
  });

  // A bar coming into view usually means the drawer just opened.
  var viewObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) { scheduleRefresh(); break; }
        }
      })
    : null;

  /* ------------------------------------------------------ custom element */

  class FreeShippingBar extends HTMLElement {
    connectedCallback() {
      this.threshold = parseInt(this.dataset.threshold, 10) || 0;
      this.currency = this.dataset.currency || null;
      this.moneyFormat = this.dataset.moneyFormat;
      this.hideWhenEmpty = this.dataset.hideWhenEmpty === 'true';
      this.msgAway = this.dataset.msgAway || 'Add [amount] more to unlock free shipping!';
      this.msgUnlocked = this.dataset.msgUnlocked || "You've unlocked free shipping!";
      this.messageEl = this.querySelector('[data-fsb-message]');
      this.trackEl = this.querySelector('[data-fsb-track]');
      this.fillEl = this.querySelector('[data-fsb-fill]');

      if (!this.threshold || !this.messageEl || !this.fillEl) return;

      instances.add(this);
      if (viewObserver) viewObserver.observe(this);

      // Liquid already rendered the correct values for this HTML.
      var serverCart = {
        total_price: parseInt(this.dataset.subtotal, 10) || 0,
        item_count: parseInt(this.dataset.itemCount, 10) || 0,
        currency: this.currency
      };
      var target = this.progressFor(serverCart.total_price);

      // If a previous bar (e.g. before a drawer re-render) showed a different
      // value, start there so the change animates instead of snapping.
      if (state.lastProgress !== null && Math.abs(state.lastProgress - target) > 0.001) {
        this.classList.add('fsb--no-transition');
        this.style.setProperty('--fsb-progress', String(state.lastProgress));
        this.fillEl.getBoundingClientRect(); // commit the start position
        this.classList.remove('fsb--no-transition');
        var self = this;
        requestAnimationFrame(function () { self.update(serverCart); });
      } else {
        this.update(serverCart);
      }

      // Confirm against the live cart (cheap, debounced, shared across bars).
      scheduleRefresh();
    }

    disconnectedCallback() {
      instances.delete(this);
      if (viewObserver) viewObserver.unobserve(this);
    }

    progressFor(subtotal) {
      return Math.max(0, Math.min(1, subtotal / this.threshold));
    }

    update(cart) {
      if (!cart || !this.threshold) return;

      // Currency changed without a reload: the SGD threshold no longer applies.
      var wrongCurrency = cart.currency && this.currency && cart.currency !== this.currency;
      var shouldHide = wrongCurrency || (this.hideWhenEmpty && cart.item_count === 0);
      if (shouldHide !== this.hidden) this.hidden = shouldHide;
      if (wrongCurrency) return;

      var subtotal = cart.total_price;
      var remaining = this.threshold - subtotal;
      var progress = this.progressFor(subtotal);
      var unlocked = remaining <= 0;

      var message = unlocked
        ? this.msgUnlocked
        : this.msgAway.replace('[amount]', formatMoney(remaining, this.moneyFormat));

      // Only touch the DOM when something actually changed.
      if (this.messageEl.textContent !== message) this.messageEl.textContent = message;
      this.classList.toggle('is-unlocked', unlocked);
      this.style.setProperty('--fsb-progress', progress.toFixed(4));
      if (this.trackEl) this.trackEl.setAttribute('aria-valuenow', String(Math.round(progress * 100)));

      state.lastProgress = progress;
    }
  }

  if (!customElements.get('free-shipping-bar')) {
    customElements.define('free-shipping-bar', FreeShippingBar);
  }

  window.TwoTownsFreeShipping = {
    refresh: scheduleRefresh,
    update: applyCart
  };
})();
